import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  devSignIn,
  exchangeGoogleCredential,
  fetchAuthConfig,
  homeFor,
  isAuthenticated,
  passwordSignIn,
  returnAllowedFor,
  type AuthConfig,
} from '../auth'
import { paths } from '../routes'

/** Minimal type for Google Identity Services. The full SDK is loaded via the
 * script tag in index.html and exposed at `window.google.accounts.id`. */
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (cfg: {
            client_id: string
            callback: (resp: { credential: string }) => void
            ux_mode?: 'popup' | 'redirect'
            auto_select?: boolean
          }) => void
          renderButton: (el: HTMLElement, opts: Record<string, unknown>) => void
          prompt: () => void
        }
      }
    }
  }
}

/** Where to land after sign-in: the remembered page, when this kind of user
 *  may be there (a customer never lands in the staff app), else their home. */
function returnTo(): string {
  let target: string | null = null
  try {
    target = sessionStorage.getItem('auth.returnTo')
    sessionStorage.removeItem('auth.returnTo')
  } catch {
    // Blocked storage: no remembered page.
  }
  return target && target !== '/login' && returnAllowedFor(target) ? target : homeFor()
}

export function Login() {
  const buttonRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  const [config, setConfig] = useState<AuthConfig | null>(null)
  const [devEmail, setDevEmail] = useState('test@allcode.com')
  const [devBusy, setDevBusy] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [passwordBusy, setPasswordBusy] = useState(false)
  // Read once: RequireAuth sets this when it forced a re-login because the token
  // was about to expire. Clear it so it only shows for this visit.
  const [sessionExpiring] = useState(() => {
    const reason = sessionStorage.getItem('auth.reason')
    sessionStorage.removeItem('auth.reason')
    return reason === 'expiring'
  })

  useEffect(() => {
    if (isAuthenticated()) {
      navigate(returnTo(), { replace: true })
      return
    }
    fetchAuthConfig()
      .then(setConfig)
      .catch((e: Error) => setError(e.message))
  }, [navigate])

  useEffect(() => {
    if (!config?.google_client_id || !buttonRef.current) return

    // The GSI script is loaded async — wait for it.
    let timer: ReturnType<typeof setTimeout> | undefined
    const waitForGoogle = (attempts = 0): void => {
      if (window.google?.accounts?.id) {
        window.google.accounts.id.initialize({
          client_id: config.google_client_id,
          callback: async (resp: { credential: string }) => {
            try {
              await exchangeGoogleCredential(resp.credential)
              navigate(returnTo(), { replace: true })
            } catch (e) {
              setError((e as Error).message)
            }
          },
          ux_mode: 'popup',
        })
        window.google.accounts.id.renderButton(buttonRef.current!, {
          theme: 'outline',
          size: 'large',
          text: 'signin_with',
          shape: 'rectangular',
          width: 320,
        })
      } else if (attempts < 50) {
        timer = setTimeout(() => waitForGoogle(attempts + 1), 100)
      } else {
        setError('Failed to load Google sign-in. Check your network and refresh.')
      }
    }
    waitForGoogle()
    return () => clearTimeout(timer)
  }, [config, navigate])

  return (
    <div
      style={{
        minHeight: '70vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 420,
          padding: '2.5rem 2rem',
          border: '1px solid var(--border)',
          borderRadius: 8,
          background: 'var(--surface)',
          textAlign: 'center',
        }}
      >
        <img
          src="/brand/allcode-logo.png"
          alt="AllCode"
          style={{ height: 48, marginBottom: '1.5rem' }}
        />
        <h1 style={{ fontSize: '1.4rem', marginBottom: '0.5rem' }}>AI Assessment</h1>
        <p style={{ color: 'var(--muted)', marginBottom: '2rem', fontSize: '0.9rem' }}>
          Sign in with your email and password, or with Google.
          {config?.allowed_domain && (
            <>
              <br />
              AllCode staff: use your @{config.allowed_domain} Google account.
            </>
          )}
        </p>

        {sessionExpiring && (
          <div
            data-testid="session-expiring-notice"
            style={{
              marginBottom: '1.25rem',
              padding: '0.75rem',
              background: 'var(--warn-soft)',
              border: '1px solid var(--warn-border)',
              borderRadius: 6,
              color: 'var(--warn)',
              fontSize: '0.85rem',
              textAlign: 'left',
            }}
          >
            Your session was about to expire. Please sign in again to continue —
            you'll return to where you left off.
          </div>
        )}

        {!config && !error && (
          <div style={{ color: 'var(--muted)' }}>Loading…</div>
        )}

        {config && !config.google_client_id && (
          <div
            style={{
              padding: '1rem',
              background: 'var(--warn-soft)',
              border: '1px solid var(--warn-border)',
              color: 'var(--fg)',
              borderRadius: 6,
              fontSize: '0.85rem',
              textAlign: 'left',
            }}
          >
            Google sign-in isn't configured. Set <code>GOOGLE_CLIENT_ID</code> in the backend
            environment.
          </div>
        )}

        {config && config.password_login_enabled !== false && (
          <form
            data-testid="password-signin-form"
            className="login-password-form"
            onSubmit={async (e) => {
              e.preventDefault()
              setError(null)
              setPasswordBusy(true)
              try {
                await passwordSignIn(email.trim(), password)
                navigate(returnTo(), { replace: true })
              } catch (err) {
                setError((err as Error).message)
              } finally {
                setPasswordBusy(false)
              }
            }}
          >
            <label>
              Email
              <input
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="password-signin-email"
                required
              />
            </label>
            <label>
              Password
              <input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="password-signin-password"
                required
              />
            </label>
            <button
              type="submit"
              className="primary"
              disabled={passwordBusy || !email.trim() || !password}
              data-testid="password-signin-submit"
            >
              {passwordBusy ? 'Signing in…' : 'Sign in'}
            </button>
            <Link to={paths.forgotPassword()} className="login-forgot" data-testid="forgot-password-link">
              Forgot password?
            </Link>
          </form>
        )}

        {config?.google_client_id && <div className="login-divider">or</div>}

        <div
          ref={buttonRef}
          style={{ display: 'flex', justifyContent: 'center', minHeight: 44 }}
          data-testid="google-signin-button"
        />

        {config?.dev_auth_enabled && (
          <div
            style={{
              marginTop: '1.5rem',
              paddingTop: '1.5rem',
              borderTop: '1px dashed var(--border)',
              textAlign: 'left',
            }}
            data-testid="dev-signin-panel"
          >
            <div
              style={{
                fontSize: '0.75rem',
                color: 'var(--muted)',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                marginBottom: '0.5rem',
              }}
            >
              Dev sign-in (fake IdP)
            </div>
            <form
              onSubmit={async (e) => {
                e.preventDefault()
                setError(null)
                setDevBusy(true)
                try {
                  await devSignIn(devEmail.trim())
                  navigate(returnTo(), { replace: true })
                } catch (err) {
                  setError((err as Error).message)
                } finally {
                  setDevBusy(false)
                }
              }}
              style={{ display: 'flex', gap: '0.5rem' }}
            >
              <input
                type="email"
                value={devEmail}
                onChange={(e) => setDevEmail(e.target.value)}
                placeholder={`name@${config.allowed_domain}`}
                data-testid="dev-signin-email"
                style={{
                  flex: 1,
                  padding: '0.45rem 0.6rem',
                  fontSize: '0.85rem',
                  border: '1px solid var(--border)',
                  borderRadius: 4,
                }}
              />
              <button
                type="submit"
                disabled={devBusy || !devEmail.trim()}
                data-testid="dev-signin-submit"
                style={{
                  padding: '0.45rem 0.85rem',
                  fontSize: '0.85rem',
                  background: 'var(--neutral-soft)',
                  border: '1px solid var(--border)',
                  borderRadius: 4,
                  cursor: devBusy ? 'wait' : 'pointer',
                }}
              >
                {devBusy ? '…' : 'Sign in'}
              </button>
            </form>
          </div>
        )}

        {error && (
          <div
            style={{
              marginTop: '1rem',
              padding: '0.75rem',
              background: 'var(--danger-soft)',
              border: '1px solid var(--danger-border)',
              borderRadius: 6,
              color: 'var(--danger)',
              fontSize: '0.85rem',
            }}
          >
            {error}
          </div>
        )}

        {/* Existing customers sign in above (invited accounts); prospective
            customers onboard themselves through the public flow. */}
        <div
          data-testid="newcustomer-cta"
          style={{
            marginTop: '2rem',
            paddingTop: '1.25rem',
            borderTop: '1px solid var(--border)',
            fontSize: '0.9rem',
            color: 'var(--muted)',
          }}
        >
          <div style={{ marginBottom: '0.6rem' }}>
            <strong style={{ color: 'var(--fg)' }}>New customer?</strong> Set up your company profile
            and connect your AWS account for a read-only scan. No sign-in needed.
          </div>
          <Link to={paths.newCustomer()} className="button-link primary" data-testid="newcustomer-link">
            Set up your company profile →
          </Link>
        </div>
      </div>
    </div>
  )
}
